import React from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { useHistory, useParams } from 'react-router-dom';
import { Box, Button, CircularProgress, Grid } from '@mui/material';
import toast from 'react-hot-toast';
import StickyHeader from 'pages/provisioning/_components/StickyHeader';
import axios, { endPoints } from 'api/request';
import { useMutation } from 'react-query';
import Empty from 'components/empty';
import { getPayload, formOrderObj, handleReset } from 'pages/user-preferences/utils/getPayload';
import { OrderPayload, toggleCreateRule, ruleListState, isRuleNameValid, isValidCostCenter } from 'pages/user-preferences/store';
import style from 'pages/user-preferences/style';
import { catalogs, riCatalogs } from 'store';
import { isReadyToSubmit } from 'pages/user-preferences/utils/formValidation';
import ApprovalsRuleForm from './approvalsRuleForm';

export const Approvals = () => {
    const { id, operation } = useParams();
    const history = useHistory();
    const [order, setOrder] = useRecoilState(OrderPayload);
    const [ruleList, setRuleList] = useRecoilState(ruleListState);
    const [cmpCatalogs] = useRecoilState(catalogs);
    const [cmpRiCatalogs] = useRecoilState(riCatalogs);
    const [modal, setModal] = useRecoilState(toggleCreateRule);
    const ruleNameValid = useRecoilValue(isRuleNameValid);
    const costCenterValid = useRecoilValue(isValidCostCenter);
    const fetchRuleListApi = useMutation(() => axios.get(endPoints?.staticLookups?.autoApprovalRules));
    const createApi = useMutation((payload) => axios.post(`${endPoints?.staticLookups?.autoApprovalRule}`, payload));
    const updateApi = useMutation((payload) => axios.put(`${endPoints?.staticLookups?.autoApprovalRule}`, payload));

    const handleSubmit = () => {
        const payload = getPayload(operation, order);
        if (operation === 'create') {
            createApi.mutate(payload, {
                onSuccess: () => {
                    setModal({ ...modal, type: null });
                    history.push('/user-preferences');
                },
            });
        }
        if (operation === 'edit') {
            updateApi.mutate(payload, {
                onSuccess: () => {
                    setModal({ ...modal, type: null });
                    history.push('/user-preferences');
                },
            });
        }
    };

    const handleCancel = () => {
        setOrder(handleReset());
        history.push('/user-preferences');
    };
    const getRuleById = () => {
        return ruleList?.find((rule) => rule?.id === id);
    };
    const prePopulateForm = async () => {
        const rule = getRuleById();
        const data = await formOrderObj(rule, cmpCatalogs, cmpRiCatalogs);
        setOrder(data);
    };
    React.useEffect(() => {
        const fetchData = async () => {
            await fetchRuleListApi?.mutate();
        };
        if (operation === 'edit') fetchData();
    }, []);
    React.useEffect(() => {
        const populateForm = async () => {
            await prePopulateForm();
        };
        if (ruleList?.length && operation === 'edit') {
            populateForm();
        }
    }, [ruleList]);
    React.useEffect(() => {
        if (fetchRuleListApi?.data) {
            setRuleList(fetchRuleListApi?.data?.data?.preApprovalRules);
        }
    }, [fetchRuleListApi?.data]);
    React.useEffect(() => {
        if (createApi?.isSuccess) {
            setModal({ ...modal, isOpen: false });
            toast.success(createApi?.data?.messages);
        }
    }, [createApi?.isSuccess]);

    React.useEffect(() => {
        if (updateApi?.isSuccess) {
            toast.success(updateApi?.data?.messages?.[0]);
            setModal({ ...modal, isOpen: false });
        }
    }, [updateApi?.isSuccess]);

    const validationStates = {
        ruleNameValid,
        costCenterValid,
    };
    const handleValidation = () => {
        const validationStatus = isReadyToSubmit(order, validationStates);
        if (validationStatus?.isValid) {
            handleSubmit();
        } else {
            setOrder({ ...order, isTouched: true });
        }
    };

    return (
        <>
            <Box>
                <StickyHeader
                    title="Create your Approval Rule"
                    showCartBtn
                    width="100%"
                    cartBtn={
                        <>
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Box sx={{ marginRight: '16px' }}>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        onClick={() => handleValidation()}
                                        fullWidth
                                        sx={style.createApprovalButton}
                                        data-testid="create-approval-btn"
                                        disabled={
                                            createApi?.isLoading ||
                                            updateApi?.isLoading ||
                                            !isReadyToSubmit(order, validationStates)?.isValid ||
                                            order?.ruleRows?.some((row) => row?.ruleType === null || row?.value === null)
                                        }
                                    >
                                        {operation === 'create' ? 'Save' : 'Save Changes'}
                                        {(createApi?.isLoading || updateApi?.isLoading) && <CircularProgress color="inherit" size={14} />}
                                    </Button>
                                </Box>
                                <Box>
                                    <Button
                                        variant="outlined"
                                        size="large"
                                        onClick={handleCancel}
                                        fullWidth
                                        sx={style.cancelApprovalButton}
                                        data-testid="cancel-approval-btn"
                                    >
                                        Cancel
                                    </Button>
                                </Box>
                            </Box>
                        </>
                    }
                />
            </Box>
            {fetchRuleListApi?.isLoading ? (
                <Empty isLoading={fetchRuleListApi?.isLoading} msg="Loading..." color="#000000" />
            ) : (
                <Grid container className="contentContainer">
                    <Grid item xs={12}>
                        <Grid display="flex" flexDirection="column" marginY={4}>
                            <ApprovalsRuleForm />
                        </Grid>
                    </Grid>
                </Grid>
            )}
        </>
    );
};

export default Approvals;
